import { useTheme } from "@mui/material";
import { Clock, type LucideIcon, PiggyBank } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useAgentQuery } from "web_myplann/Api/Advisor/useAgentQuery";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { device } from "web_myplann/Utils/device";

export const EXTRA_PRESETS = [500, 1000, 2000, 5000];
export const EXTRA_MAX = 10_000;
export const EXTRA_STEP = 100;

export type WhatIfCard = "earlier" | "more";

export type WhatIfCardInfo = {
	label: string;
	subtitle: string;
	accent: string;
	icon: LucideIcon;
};

export type WhatIf = {
	visible: boolean;
	extraAmount: number;
	setExtraAmount: (value: number) => void;
	activeCard: WhatIfCard;
	setActiveCard: (card: WhatIfCard) => void;
	cards: WhatIfCard[];
	cardInfo: Record<WhatIfCard, WhatIfCardInfo>;
	currentYear: number;
	dueYear: number;
	monthsSaved: number;
	newYear: number;
	newPct: number;
	savingsTarget: number;
	extraTotal: number;
	newTargetTotal: number;
	savedLabel: string;
	openChat: () => void;
};

export const useWhatIf = (goal: GoalDetail): WhatIf => {
	const theme = useTheme();
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();
	const { data: agent } = useAgentQuery(goal.agent?.id ?? 0);

	const [extraAmount, setExtraAmount] = useState(EXTRA_PRESETS[1]);
	const canShowEarlier = goal.monthlyContribution > 0;
	const [activeCard, setActiveCard] = useState<WhatIfCard>(
		canShowEarlier ? "earlier" : "more",
	);

	const openChat = useCallback(() => {
		device.navigateChatRequest(
			String(agent?.conversationId ?? ""),
			agent?.conversationTitle ?? agent?.name ?? goal.agent?.name ?? "",
			agent?.uid,
		);
	}, [agent, goal.agent?.name]);

	return useMemo(() => {
		const currentYear = new Date().getFullYear();
		const totalSpan = goal.dueYear - currentYear;
		const monthsLeft = Math.max(1, totalSpan * 12);
		const monthly = goal.monthlyContribution;
		const ratio = monthly > 0 ? monthly / (monthly + extraAmount) : 1;

		const monthsSaved =
			extraAmount > 0 && monthly > 0
				? monthsLeft - Math.round(monthsLeft * ratio)
				: 0;
		const newYear =
			extraAmount > 0 && monthly > 0
				? Math.round(currentYear + (monthsLeft * ratio) / 12)
				: goal.dueYear;
		const newPct =
			totalSpan > 0
				? Math.min(95, Math.max(5, ((newYear - currentYear) / totalSpan) * 100))
				: 95;
		const extraTotal = extraAmount > 0 ? extraAmount * monthsLeft : 0;

		const years = Math.floor(monthsSaved / 12);
		const months = monthsSaved % 12;
		const savedLabel =
			[
				years > 0
					? t(Resources.Goals.Detail.WhatIf.Earlier.years, { count: years })
					: "",
				months > 0
					? t(Resources.Goals.Detail.WhatIf.Earlier.months, { count: months })
					: "",
			]
				.filter(Boolean)
				.join(` ${t(Resources.Goals.Detail.WhatIf.Earlier.and)} `) ||
			t(Resources.Goals.Detail.WhatIf.Earlier.none);

		const cards: WhatIfCard[] = canShowEarlier ? ["earlier", "more"] : ["more"];

		const cardInfo: Record<WhatIfCard, WhatIfCardInfo> = {
			earlier: {
				label: t(Resources.Goals.Detail.WhatIf.Earlier.label),
				subtitle: t(Resources.Goals.Detail.WhatIf.Earlier.subtitle, {
					saved: savedLabel,
				}),
				accent: theme.palette.success.main,
				icon: Clock,
			},
			more: {
				label: t(Resources.Goals.Detail.WhatIf.More.label),
				subtitle: t(Resources.Goals.Detail.WhatIf.More.subtitle, {
					amount: formatAmount(extraTotal, goal.currencyCode),
				}),
				accent: theme.palette.blue.main,
				icon: PiggyBank,
			},
		};

		return {
			visible: !goal.completed && goal.savingsTarget > 0 && totalSpan > 0,
			extraAmount,
			setExtraAmount,
			activeCard: cards.includes(activeCard) ? activeCard : cards[0],
			setActiveCard,
			cards,
			cardInfo,
			currentYear,
			dueYear: goal.dueYear,
			monthsSaved,
			newYear,
			newPct,
			savingsTarget: goal.savingsTarget,
			extraTotal,
			newTargetTotal: goal.savingsTarget + extraTotal,
			savedLabel,
			openChat,
		};
	}, [
		goal,
		extraAmount,
		activeCard,
		canShowEarlier,
		t,
		theme,
		formatAmount,
		openChat,
	]);
};
